import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Preferences } from '@capacitor/preferences';

export interface TripQrPayload {
  tripId: number;
  lineName: string;
  plate: string;
}

export interface CheckinResult {
  message: string;
  boardingId: number;
  lineName: string;
  plate: string;
}

export interface CheckoutResult {
  message: string;
  checkedOutAt: string;
}

/** Persisted after a successful check-in; cleared on check-out. */
export interface ActiveBoarding {
  boardingId: number;
  tripId: number;
  lineName: string;
  plate: string;
  scannedAt: string; // ISO string
}

const STORAGE_KEY = 'active-boarding';

@Injectable({ providedIn: 'root' })
export class BoardingService {
  private readonly http = inject(HttpClient);

  /** Get the current user's permanent passenger QR token */
  async getMyQrToken(): Promise<string> {
    const res = await firstValueFrom(
      this.http.get<{ qrToken: string }>('/boardings/my-qr'),
    );
    return res.qrToken;
  }

  /** Self check-in: passenger scanned the driver's trip QR */
  async checkin(tripId: number, lat?: number, lng?: number): Promise<CheckinResult> {
    const body: Record<string, number> = {};
    if (lat != null && lng != null) { body['lat'] = lat; body['lng'] = lng; }
    return firstValueFrom(
      this.http.post<CheckinResult>(`/boardings/trip/${tripId}/checkin`, body),
    );
  }

  /** Pay with balance after check-in. Returns updated passenger balance. */
  async payWithBalance(boardingId: number, amount: number): Promise<{ message: string; newBalance: string }> {
    return firstValueFrom(
      this.http.post<{ message: string; newBalance: string }>(
        `/boardings/${boardingId}/pay-with-balance`,
        { amount },
      ),
    );
  }

  /** Check-out: passenger taps "Salir del bus" — no payment involved. */
  async checkout(boardingId: number, lat?: number, lng?: number): Promise<CheckoutResult> {
    const body: Record<string, number> = {};
    if (lat != null && lng != null) { body['lat'] = lat; body['lng'] = lng; }
    return firstValueFrom(
      this.http.patch<CheckoutResult>(`/boardings/${boardingId}/checkout`, body),
    );
  }

  // ── Active boarding persistence (Capacitor Preferences) ─────────────────

  async saveActiveBoarding(boarding: ActiveBoarding): Promise<void> {
    await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify(boarding) });
  }

  async loadActiveBoarding(): Promise<ActiveBoarding | null> {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return null;
    try { return JSON.parse(value) as ActiveBoarding; } catch { return null; }
  }

  async clearActiveBoarding(): Promise<void> {
    await Preferences.remove({ key: STORAGE_KEY });
  }
}
